import type {Task} from '../../types'
import {useMemo} from 'react'
import {
  MessageSquare,
  GitCommitVertical as GitCommit,
  CheckCircle2,
  AlertCircle,
  Upload,
  FileText,
} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import Avatar from '../common/Avatar'
import {formatRelativeDate} from '../../utils/helpers'

interface Activity {
  id: string
  taskId: string
  userId: string
  action: string
  target: string
  type: 'comment' | 'status' | 'complete' | 'create' | 'upload'
  createdAt: string
}

const activityIcons = {
  comment: MessageSquare,
  status: GitCommit,
  complete: CheckCircle2,
  create: AlertCircle,
  upload: Upload,
}

const activityColors = {
  comment: 'text-brand-500 bg-brand-50',
  status: 'text-amber-500 bg-amber-50',
  complete: 'text-emerald-500 bg-emerald-50',
  create: 'text-cyan-500 bg-cyan-50',
  upload: 'text-slate-500 bg-slate-100',
}

export default function ActivityFeed({tasks}: {tasks?: Task[]}) {
  const {state, getUserById, dispatch} = useApp()

  const activities = useMemo<Activity[]>(() => {
    const items: Activity[] = []

    for (const task of tasks || state.tasks) {
      if (task.createdAt && task.reporterId) {
        items.push({
          id: `create-${task.id}`,
          taskId: task.id,
          userId: task.reporterId,
          action: 'criou a tarefa',
          target: task.title,
          type: 'create',
          createdAt: task.createdAt,
        })
      }

      for (const att of task.attachments) {
        items.push({
          id: `att-${att.id}`,
          taskId: task.id,
          userId: att.uploadedBy || task.reporterId,
          action: 'enviou um arquivo para',
          target: task.title,
          type: 'upload',
          createdAt: att.uploadedAt || task.updatedAt,
        })
      }

      for (const comment of task.comments) {
        items.push({
          id: `comment-${comment.id}`,
          taskId: task.id,
          userId: comment.userId,
          action: 'comentou em',
          target: task.title,
          type: 'comment',
          createdAt: comment.createdAt,
        })
      }
    }

    return items
      .filter((a) => !!a.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10)
  }, [state.tasks, tasks])

  const handleOpenTask = (taskId: string) => {
    dispatch({type: 'SELECT_TASK', payload: taskId})
  }

  if (state.loading) {
    return (
      <div className='devops-surface p-4 md:p-5 flex items-center justify-center h-48'>
        <div className='flex flex-col items-center gap-2 text-slate-400'>
          <div className='w-6 h-6 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin' />
          <span className='text-xs'>Carregando atividade...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='devops-surface p-4 md:p-5'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-sm font-semibold text-slate-700'>Atividade Recente</h3>
        <FileText size={16} className='text-slate-400' />
      </div>

      {activities.length === 0 ? (
        <div className='flex items-center justify-center h-32 text-sm text-slate-400'>
          Nenhuma atividade recente
        </div>
      ) : (
        <div className='space-y-4'>
          {activities.map((activity) => {
            const user = getUserById(activity.userId)
            const Icon = activityIcons[activity.type]
            const colorClass = activityColors[activity.type]

            return (
              <div
                key={activity.id}
                className='flex gap-3 group cursor-pointer rounded-lg p-1.5 -m-1.5 transition-colors hover:bg-slate-50'
                onClick={() => handleOpenTask(activity.taskId)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenTask(activity.taskId)
                  }
                }}
                aria-label={`Abrir tarefa: ${activity.target}`}
              >
                <div className='relative shrink-0'>
                  <Avatar
                    name={user?.name || 'Usuário'}
                    color={user?.avatarColor || '#94a3b8'}
                    size='md'
                  />
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white ${colorClass}`}
                  >
                    <Icon size={8} />
                  </div>
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm text-slate-600 leading-snug'>
                    <span className='font-medium text-slate-700'>{user?.name || 'Usuário'}</span>{' '}
                    {activity.action}{' '}
                    <span className='font-medium text-brand-600 group-hover:underline underline-offset-2'>
                      {activity.target}
                    </span>
                  </p>
                  <p className='text-xs text-slate-400 mt-0.5'>
                    {formatRelativeDate(activity.createdAt)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
